/**
 * Chat message rendering registry.
 *
 * Architecture: The orchestrator is the sole agent that streams to the user.
 * Sub-agent output (e.g., plan_campaign's planner summary) is NEVER shown directly —
 * it arrives as a tool-output-available chunk and is rendered inside a ReasoningToggle
 * ("Show Reasoning" collapsible) for transparency/debugging only. The user sees only
 * the orchestrator's own text response + UI tool calls (forms, option buttons).
 */
import React from 'react';
import { TextMessage } from './TextMessage';
import { OptionsMessage } from './OptionsMessage';
import { FormMessage } from './FormMessage';
import { ReasoningToggle } from './ReasoningToggle';
import { ToolIndicator } from './ToolIndicator';
import { registerUiToolRenderers } from './ui-tools';
import { type ChatMessageProps } from './types';

registerUiToolRenderers({
  'tool-collect_fields': FormMessage,
  'tool-show_options': OptionsMessage,
});

const ThinkingMessage: React.FC<ChatMessageProps> = ({ part }) => {
  const text = (part.reasoning ?? part.text ?? '') as string;
  return <ReasoningToggle text={text} />;
};

const MESSAGE_RENDERERS: Record<string, React.FC<ChatMessageProps>> = {
  text: TextMessage,
  reasoning: ThinkingMessage,
  'tool-show_options': OptionsMessage,
  'tool-collect_fields': FormMessage,
};

export const renderMessage = (props: ChatMessageProps): React.ReactNode => {
  const type = props.part.type as string;
  const Renderer = MESSAGE_RENDERERS[type];
  if (Renderer) return <Renderer {...props} />;
  if (type === 'tool-call' || type === 'source') return null;
  // Sub-agent streaming: show live progress via ToolIndicator
  if (type?.startsWith('tool-') && props.part.state === 'output-available' && props.part.preliminary === true) {
    return <ToolIndicator {...props} />;
  }
  // Internal tool output (e.g., plan_campaign) — show in reasoning toggle, not directly to user
  if (type?.startsWith('tool-') && props.part.state === 'output-available') {
    const output = props.part.output;
    const text = typeof output === 'string' ? output : '';
    return text.trim() ? <ReasoningToggle text={text} /> : null;
  }
  if (type?.startsWith('tool-')) return <ToolIndicator {...props} />;
  return null;
};

export type { ChatMessageProps } from './types';
